/**
 * Voice Chat System for Veridian Companion
 * Handles: Mic → Speech-to-Text → Claude → ElevenLabs TTS → Audio playback
 * Uses Web Speech API for recognition (free, browser-built-in)
 */

export class VoiceChat {
  constructor(options = {}) {
    this.onTranscript = options.onTranscript || (() => {});  // Called with final transcript text
    this.onListening = options.onListening || (() => {});    // Called when mic starts/stops
    this.onError = options.onError || (() => {});

    this.recognition = null;
    this.isListening = false;
    this.supported = false;

    this._initRecognition();
  }

  _initRecognition() {
    const SpeechRecognition = window.SpeechRecognition || window.webkitSpeechRecognition;
    if (!SpeechRecognition) {
      console.warn('⚠ Speech recognition not supported in this browser');
      this.supported = false;
      return;
    }

    this.supported = true;
    this.recognition = new SpeechRecognition();
    this.recognition.continuous = false;       // Stop after one utterance
    this.recognition.interimResults = true;    // Show partial results
    this.recognition.lang = 'en-US';
    this.recognition.maxAlternatives = 1;

    this.recognition.onstart = () => {
      this.isListening = true;
      this.onListening(true);
    };

    this.recognition.onresult = (event) => {
      let interimTranscript = '';
      let finalTranscript = '';

      for (let i = event.resultIndex; i < event.results.length; i++) {
        const transcript = event.results[i][0].transcript;
        if (event.results[i].isFinal) {
          finalTranscript += transcript;
        } else {
          interimTranscript += transcript;
        }
      }

      // Show interim results in the input field
      if (this._inputEl && interimTranscript) {
        this._inputEl.value = interimTranscript;
        this._inputEl.classList.add('voice-interim');
      }

      // When we get a final result, send it
      if (finalTranscript.trim()) {
        if (this._inputEl) {
          this._inputEl.value = '';
          this._inputEl.classList.remove('voice-interim');
        }
        this.onTranscript(finalTranscript.trim());
      }
    };

    this.recognition.onerror = (event) => {
      console.warn('Speech recognition error:', event.error);
      this.isListening = false;
      this.onListening(false);

      if (event.error === 'not-allowed') {
        this.onError('Microphone access denied. Please allow mic access in your browser settings.');
      } else if (event.error !== 'aborted' && event.error !== 'no-speech') {
        this.onError(`Voice error: ${event.error}`);
      }
    };

    this.recognition.onend = () => {
      this.isListening = false;
      this.onListening(false);
    };
  }

  /** Bind to an input element to show interim results */
  bindInput(inputEl) {
    this._inputEl = inputEl;
  }

  /** Start listening */
  start() {
    if (!this.supported) {
      this.onError('Speech recognition not supported in this browser. Try Chrome.');
      return;
    }
    if (this.isListening) return;

    try {
      this.recognition.start();
    } catch (e) {
      console.warn('Recognition start failed:', e);
    }
  }

  /** Stop listening */
  stop() {
    if (!this.recognition || !this.isListening) return;
    try {
      this.recognition.stop();
    } catch (e) {
      console.warn('Recognition stop failed:', e);
    }
  }

  /** Toggle listening on/off */
  toggle() {
    if (this.isListening) {
      this.stop();
    } else {
      this.start();
    }
  }

  /** Push-to-talk: start on keydown, stop on keyup */
  enablePushToTalk(key = 'v') {
    let held = false;
    window.addEventListener('keydown', (e) => {
      if (e.key.toLowerCase() !== key) return;
      // Don't activate if typing in an input
      const tag = (e.target.tagName || '').toUpperCase();
      if (tag === 'INPUT' || tag === 'TEXTAREA' || e.target.isContentEditable) return;
      if (held) return;
      held = true;
      this.start();
    });

    window.addEventListener('keyup', (e) => {
      if (e.key.toLowerCase() !== key) return;
      if (!held) return;
      held = false;
      // Small delay to catch last words
      setTimeout(() => this.stop(), 300);
    });
  }

  destroy() {
    if (this.recognition) {
      this.recognition.abort();
      this.recognition = null;
    }
  }
}
